// Two-party transactions (WAF-3235), created in the portal: a project with one milestone where the
// organization is the seller (PROVIDER) and one customer pays. Waffy acts as the broker without
// being a party, so its fee is not anyone's cash-out — at settlement it has to be taken out of the
// paid amount explicitly, which is what these helpers are for.
import {
  CashOutAmount,
  EPaymentBrand,
  ESenderRole,
  IContractDetailsResponseData,
} from "../api/services/contract/models";
import {
  IOrganizationFeeTemplate,
  IOrganizationPaymentFee,
} from "../api/services/organization/models";
import { resolveCashoutPartyRole } from "./userIdentifier";

/** Set by the portal on the project and on its milestone. */
export const isTwoPartyContract = (
  contract?: Pick<IContractDetailsResponseData, "metaData"> | null
): boolean =>
  (contract?.metaData as Record<string, unknown> | undefined)
    ?.transactionKind === "TWO_PARTY";

const roundToCents = (value: number) => Math.round(value * 100) / 100;

const feeFor = (payment: IOrganizationPaymentFee, amount: number) =>
  payment.feeType === "PERCENTAGE"
    ? amount * (payment.fees || 0)
    : payment.fees || 0;

/** Every brand the buyer was offered, as the portal's fee preview counts them. */
const offeredBrands = (
  contract: Pick<IContractDetailsResponseData, "paymentMethods">
): string[] => {
  const brands = Object.values(
    contract.paymentMethods || {}
  ).flat() as string[];
  // Apple Pay is offered with the cards.
  if (
    brands.some((brand) => ["MADA", "VISA", "MASTER", "AMEX"].includes(brand))
  )
    brands.push(EPaymentBrand.APPLE_PAY);
  return brands;
};

export interface IWaffyFee {
  amount: number;
  /** Of the paid amount, for display. */
  percentage: number;
  /** Where the amount came from. */
  source: "contract" | "feeTemplate";
}

/**
 * Waffy's fee on a two-party transaction, in the order every settlement already uses: the fee the
 * backend stored on the contract (`fee`, as the milestone pages show it; C2C reads the contract's
 * `feePercentage` the same way). Only when the contract has none is it computed from the
 * organization's fee template — the tier the paid amount falls in, at the highest rate among the
 * payment methods the buyer was offered (WAF-3235, as the portal previews it to the seller).
 */
export const getTwoPartyWaffyFee = (
  contract: Pick<IContractDetailsResponseData, "fee" | "paymentMethods">,
  paidAmount: number,
  feeTemplate?: IOrganizationFeeTemplate
): IWaffyFee | undefined => {
  if (!paidAmount || paidAmount <= 0) return undefined;
  const percentageOf = (amount: number) =>
    roundToCents((amount / paidAmount) * 100);

  const contractFee = Number(contract.fee);
  if (contractFee > 0) {
    const amount = roundToCents(contractFee);
    return { amount, percentage: percentageOf(amount), source: "contract" };
  }

  const tier = feeTemplate?.tiers?.find(
    (candidate) =>
      paidAmount >= (candidate.from ?? 0) &&
      (!candidate.to || paidAmount <= candidate.to)
  );
  const brands = offeredBrands(contract);
  const fees = (tier?.paymentTypes || [])
    .filter((payment) => brands.includes(payment.brand))
    .map((payment) => feeFor(payment, paidAmount));
  if (!fees.length) return undefined;

  const amount = roundToCents(Math.max(...fees));
  return { amount, percentage: percentageOf(amount), source: "feeTemplate" };
};

export interface ITwoPartyCashout {
  /** Waffy's fee taken from this line. */
  fee: number;
  /** What the party actually receives. */
  net: number;
}

/**
 * A two-party cash-out line's fee and payout. Settlement sends the seller's line as a gross
 * `amountDue` with Waffy's `fee`, but the backend stores it with fee 0 and `cashOutAmount` equal
 * to the gross — so when no line carries a fee, the contract's `fee` is taken from the line that
 * pays it: the seller's, or the buyer's when nothing goes to the seller (as at settlement).
 * Undefined for any other contract, whose lines are shown and paid as the backend sends them.
 */
export const getTwoPartyCashout = (
  contract: IContractDetailsResponseData | undefined,
  line: CashOutAmount
): ITwoPartyCashout | undefined => {
  if (!contract || !isTwoPartyContract(contract)) return undefined;

  const lineFee = Number(line.fee) || 0;
  if (lineFee > 0) return { fee: lineFee, net: line.cashOutAmount };

  const lines = contract.cashOutAmountList || [];
  const contractFee = Number(contract.fee) || 0;
  if (contractFee <= 0 || lines.some((other) => Number(other.fee) > 0))
    return { fee: 0, net: line.cashOutAmount };

  const paying = lines.filter((other) => (other.amountDue || 0) > 0);
  const roleOf = (other: CashOutAmount) =>
    resolveCashoutPartyRole(contract, other.userIdentification);
  const payer =
    paying.find((other) => roleOf(other) === ESenderRole.PROVIDER) ||
    paying.find((other) => roleOf(other) === ESenderRole.CUSTOMER);
  if (!payer || payer.userIdentification !== line.userIdentification)
    return { fee: 0, net: line.cashOutAmount };

  const gross = line.amountDue ?? line.cashOutAmount;
  return {
    fee: contractFee,
    net: roundToCents(Math.max(gross - contractFee, 0)),
  };
};

/** What a cash-out line pays out: net of Waffy's fee on a two-party transaction. */
export const getCashoutPayout = (
  contract: IContractDetailsResponseData | undefined,
  line: CashOutAmount
): number => getTwoPartyCashout(contract, line)?.net ?? line.cashOutAmount;
